import React, { useCallback, useEffect, useMemo, useState } from 'react';

import { SmallButton } from '@eika/button';
import { Input } from '@eika/input';
import { Tabs } from '@eika/tabs';

import { useConfig } from '../../defaultConfig';
import { Kid } from '../../generated-types';
import { useNoteMaxLengthWithKIDSelected } from '../../hooks/useNoteMaxLength';
import useTranslation, { format } from '../../hooks/useTranslation';
import { NoteTypes } from '../../translations/translationType';
import { findDefaultNoteType } from '../fields/MessageInput/MessageInput';
import { StickyFooterRow } from '../shared/StickyFooter';
import { validateKidWithMaxLength, validateNoteWithMaxLength } from '../transaction/Payment/form/validate';
import { mod11NeedsDash } from '../transaction/Payment/form/validateModService';
import KidWithDashHelper from './KidWithDashHelper';

import './Note.scss';

type SelectedReceiver = import('../transaction/SelectedReceiver').SelectedReceiver;

export enum KidWithDashStages {
    NO_DASH_NEEDED,
    SHOW_MESSAGE,
    GOT_HELP,
}

interface Props {
    noteLabel?: string;
    note?: string;
    kid?: string;
    ownText?: string;
    toAccount?: SelectedReceiver;
    onChange: (value: string) => void;
    onChangeKid: (kid: Kid | undefined) => void;
    onChangeOwnText?: (value: string) => void;
    placeholder?: string;
    ownTextLabel?: string;
    ownTextMaxLength?: number;
    noteMaxLength?: number;
    type: NoteTypes;
    showToggleKidMessage: boolean;
}

const capitalizeFirstLetterNewSentence = (newValue: string, currentValue?: string) => {
    if (currentValue === '' && newValue.length === 1) {
        return newValue.toUpperCase();
    }
    return newValue;
};

const Note: React.FC<Props> = ({
    noteLabel,
    note,
    kid,
    ownText,
    toAccount,
    onChange,
    onChangeKid,
    placeholder,
    ownTextLabel,
    ownTextMaxLength = 250,
    type,
    onChangeOwnText,
    noteMaxLength,
    showToggleKidMessage,
}) => {
    const translated = useTranslation();

    const defaultNoteType = useMemo(
        () => findDefaultNoteType(toAccount?.kidFormats, type, kid, note),
        [toAccount?.kidFormats, type, kid, note],
    );

    const [selectedNoteType, setSelectedNoteType] = useState<NoteTypes | undefined>(defaultNoteType);
    const [value, setValue] = useState<string>('');

    const [ownTextValue, setOwnTextValue] = useState<string>(ownText ?? '');
    const [error, setError] = useState<string>();
    const [errorOwnText, setErrorOwnText] = useState<string>();
    const [showKidHelpTekst, setShowKidHelpTekst] = useState<KidWithDashStages>(KidWithDashStages.NO_DASH_NEEDED);
    const { featureToggles } = useConfig();

    useEffect(() => {
        switch (selectedNoteType) {
            case NoteTypes.KID:
                setValue(kid ?? '');
                break;
            case NoteTypes.MESSAGE:
                setValue(note ?? '');
                break;
            default:
                break;
        }
    }, [note, kid, selectedNoteType]);

    useEffect(() => {
        if (!value) {
            setShowKidHelpTekst(KidWithDashStages.NO_DASH_NEEDED);
        }
    }, [value]);

    const maxLength = useNoteMaxLengthWithKIDSelected({ selectedReceiver: toAccount });
    const selectedMaxLength = noteMaxLength || maxLength;

    const handleClick = useCallback(() => {
        const findValidationError = () => {
            if (!toAccount) return undefined;
            if (selectedNoteType === NoteTypes.KID) {
                return validateKidWithMaxLength(
                    translated,
                    value,
                    maxLength,
                    toAccount,
                    featureToggles.enableKidValidation,
                );
            }
            return validateNoteWithMaxLength(translated, value, maxLength, toAccount);
        };
        const validationError = findValidationError();
        if (validationError) {
            setError(validationError);
        } else {
            if (selectedNoteType === NoteTypes.KID || toAccount?.kidRequired) {
                onChangeKid({ value });
                onChange('');
            } else {
                onChange(value);
                onChangeKid(undefined);
            }
            onChangeOwnText?.(ownTextValue);
        }
    }, [
        translated,
        value,
        maxLength,
        toAccount,
        featureToggles.enableKidValidation,
        selectedNoteType,
        onChangeOwnText,
        ownTextValue,
        onChangeKid,
        onChange,
    ]);

    const onChangeEvent = useCallback(
        (e: React.ChangeEvent<HTMLInputElement>) => {
            const { value } = e.target;
            setError(undefined);
            if (mod11NeedsDash(toAccount?.kidFormats, value)) {
                setShowKidHelpTekst(KidWithDashStages.SHOW_MESSAGE);
            }
            if (value.length > selectedMaxLength && type !== NoteTypes.KID) {
                setError(format(translated.tooManyCharacters, selectedMaxLength));
            } else if (
                value.length > selectedMaxLength &&
                type === NoteTypes.KID &&
                featureToggles.enableKidValidation
            ) {
                setError(format(translated.tooManyCharactersKID, selectedMaxLength));
            }
            const snipped = value.length >= selectedMaxLength ? value.substring(0, selectedMaxLength) : value;
            setValue(capitalizeFirstLetterNewSentence(snipped, note));
        },
        [
            toAccount?.kidFormats,
            selectedMaxLength,
            type,
            featureToggles.enableKidValidation,
            note,
            translated.tooManyCharacters,
            translated.tooManyCharactersKID,
        ],
    );

    const onChangeEventOwnText = useCallback(
        (e: React.ChangeEvent<HTMLInputElement>) => {
            const { value } = e.target;
            setErrorOwnText(undefined);
            if (value.length > ownTextMaxLength) {
                setErrorOwnText(format(translated.tooManyCharacters, ownTextMaxLength));
            }
            const snipped = value.length >= ownTextMaxLength ? value.substring(0, ownTextMaxLength) : value;
            setOwnTextValue(capitalizeFirstLetterNewSentence(snipped, ownText));
        },
        [ownText, setErrorOwnText, setOwnTextValue, ownTextMaxLength, translated.tooManyCharacters],
    );

    const onKeyDown = useCallback(
        (event: React.KeyboardEvent<HTMLDivElement>) => {
            if (event.key === 'Enter') {
                event.preventDefault();
                handleClick();
            }
        },
        [handleClick],
    );

    const handleAddDash = useCallback(() => {
        setValue(`${value}-`);
        setShowKidHelpTekst(KidWithDashStages.GOT_HELP);
    }, [value]);

    return (
        <>
            <div className="note__container" onKeyDown={onKeyDown}>
                {selectedNoteType === NoteTypes.KID ? (
                    <label className="title typography-h2 note__transaction-text_label" htmlFor="kidReceiver">
                        {translated.amountNote[NoteTypes.KID].label}
                    </label>
                ) : (
                    <label className="title typography-h2 note__transaction-text_label" htmlFor="noteReceiver">
                        {noteLabel ?? translated.amountNote[NoteTypes.MESSAGE].label}
                    </label>
                )}
                <Tabs
                    onChangeValue={(value) => {
                        setSelectedNoteType(value as NoteTypes);
                        setError(undefined);
                    }}
                    removeHiddenTabsFromDom={false}
                    defaultValue={selectedNoteType}
                >
                    {showToggleKidMessage && (
                        <Tabs.TabList>
                            <Tabs.TabButton value={NoteTypes.KID} data-testid="switch-to-KID">
                                {translated.amountNote[NoteTypes.KID].label}
                            </Tabs.TabButton>
                            <Tabs.TabButton value={NoteTypes.MESSAGE} data-testid="switch-to-message">
                                {translated.amountNote[NoteTypes.MESSAGE].label}
                            </Tabs.TabButton>
                        </Tabs.TabList>
                    )}
                    <Tabs.Tab value={NoteTypes.KID}>
                        <Input
                            id="kidReceiver"
                            name="kidReceiver"
                            className="note__input"
                            value={value}
                            placeholder={translated.amountNote[NoteTypes.KID].label}
                            maxLength={selectedMaxLength + 1}
                            onChange={onChangeEvent}
                            error={error}
                            data-focus-first
                            type="tel"
                            data-testid="KID-input"
                        />
                        <KidWithDashHelper helpStatus={showKidHelpTekst} handleAddDash={handleAddDash} />
                    </Tabs.Tab>
                    <Tabs.Tab value={NoteTypes.MESSAGE}>
                        <Input
                            id="noteReceiver"
                            name="noteReceiver"
                            className="note__input"
                            value={value}
                            placeholder={placeholder ?? translated.amountNote[NoteTypes.MESSAGE].label}
                            maxLength={selectedMaxLength + 1}
                            onChange={onChangeEvent}
                            error={error}
                            data-focus-first
                            type="text"
                            data-testid="message-input"
                        />
                    </Tabs.Tab>
                </Tabs>
                {onChangeOwnText && !featureToggles.disableOwnText && (
                    <>
                        <label className="note__own-transaction-text_label " htmlFor="noteReceiverOwnText">
                            {ownTextLabel ?? translated.paymentAddOwnTransactionText}
                        </label>
                        <Input
                            id="noteReceiverOwnText"
                            name="noteReceiverOwnText"
                            value={ownTextValue}
                            placeholder={translated.ownTextPlaceholder}
                            maxLength={ownTextMaxLength + 1}
                            onChange={onChangeEventOwnText}
                            error={errorOwnText}
                            data-focus-first
                            type="text"
                        />
                    </>
                )}
            </div>
            <StickyFooterRow>
                <SmallButton onClick={handleClick}>{translated.continue}</SmallButton>
            </StickyFooterRow>
        </>
    );
};

export default Note;
